import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Zap, Plus, Trash2, Edit, Power, PowerOff, Clock, CheckCircle, XCircle, AlertTriangle, List
} from "lucide-react";

const API = process.env.REACT_APP_API || "";

export function AutomationsView() {
  const [subTab, setSubTab] = useState("liste");
  const [automations, setAutomations] = useState([]);
  const [protokoll, setProtokoll] = useState([]);
  const [pending, setPending] = useState([]);
  const [available, setAvailable] = useState({ triggers: [], actions: [] });
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const headers = useCallback(() => {
    const t = localStorage.getItem("token") || sessionStorage.getItem("token");
    return { Authorization: `Bearer ${t}` };
  }, []);

  const fetchAutomations = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/automations`, { headers: headers() }); setAutomations(r.data || []); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchProtokoll = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/automations/protokoll`, { headers: headers() }); setProtokoll(r.data || []); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchPending = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/automations/pending`, { headers: headers() }); setPending(r.data || []); } catch (e) { console.error(e); }
  }, [headers]);

  const fetchAvailable = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/automations/available`, { headers: headers() }); setAvailable(r.data || { triggers: [], actions: [] }); } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => { fetchAutomations(); fetchAvailable(); }, [fetchAutomations, fetchAvailable]);
  useEffect(() => { if (subTab === "protokoll") fetchProtokoll(); if (subTab === "wartend") fetchPending(); }, [subTab, fetchProtokoll, fetchPending]);

  // --- Form ---
  const AutomationForm = () => {
    const [form, setForm] = useState(editItem || { name: "", trigger: "", aktionen: [{ typ: "", params: {} }], aktiv: true, bestaetigung_erforderlich: false });

    const save = async () => {
      if (!form.name || !form.trigger) { toast.error("Name und Trigger erforderlich"); return; }
      try {
        if (editItem) {
          await axios.put(`${API}/api/automations/${editItem.id}`, form, { headers: headers() });
          toast.success("Automation aktualisiert");
        } else {
          await axios.post(`${API}/api/automations`, form, { headers: headers() });
          toast.success("Automation erstellt");
        }
        setShowForm(false); setEditItem(null); fetchAutomations();
      } catch (e) { toast.error("Fehler"); }
    };

    return (
      <Card className="p-4 space-y-4">
        <h3 className="font-semibold">{editItem ? "Automation bearbeiten" : "Neue Automation"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="z.B. Rechnung überfällig → Erinnerung" /></div>
          <div><Label>WENN (Trigger)</Label>
            <Select value={form.trigger} onValueChange={(v) => setForm({...form, trigger: v})}>
              <SelectTrigger><SelectValue placeholder="Trigger wählen..." /></SelectTrigger>
              <SelectContent>
                {available.triggers.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.label} ({t.studio})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>DANN (Aktion)</Label>
            <Select value={form.aktionen[0]?.typ || ""} onValueChange={(v) => setForm({...form, aktionen: [{typ: v, params: {}}]})}>
              <SelectTrigger><SelectValue placeholder="Aktion wählen..." /></SelectTrigger>
              <SelectContent>
                {available.actions.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4 pt-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.bestaetigung_erforderlich} onChange={(e) => setForm({...form, bestaetigung_erforderlich: e.target.checked})} className="rounded" />
              Bestätigung erforderlich
            </label>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save}>Speichern</Button>
          <Button variant="outline" onClick={() => { setShowForm(false); setEditItem(null); }}>Abbrechen</Button>
        </div>
      </Card>
    );
  };

  // --- Liste ---
  const ListeView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Automationen</h2>
        <Button onClick={() => { setShowForm(true); setEditItem(null); }} className="gap-1"><Plus className="w-4 h-4" /> Neue Automation</Button>
      </div>
      {showForm && <AutomationForm />}
      <div className="space-y-2">
        {automations.length === 0 ? <p className="text-gray-400 text-sm">Keine Automationen konfiguriert</p> : automations.map((a) => (
          <Card key={a.id} className={`p-3 flex items-center justify-between ${!a.aktiv ? "opacity-50" : ""}`}>
            <div className="flex items-center gap-3">
              <Zap className={`w-4 h-4 ${a.aktiv ? "text-yellow-500" : "text-gray-300"}`} />
              <div>
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-gray-500">
                  Trigger: {available.triggers.find(t => t.id === a.trigger)?.label || a.trigger}
                  {a.ausfuehrungen > 0 && ` | ${a.ausfuehrungen}x ausgeführt`}
                  {a.bestaetigung_erforderlich && " | Bestätigung nötig"}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={async () => {
                await axios.put(`${API}/api/automations/${a.id}/toggle`, {}, { headers: headers() });
                toast.success(a.aktiv ? "Deaktiviert" : "Aktiviert"); fetchAutomations();
              }}>
                {a.aktiv ? <Power className="w-3.5 h-3.5 text-green-500" /> : <PowerOff className="w-3.5 h-3.5 text-gray-400" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditItem(a); setShowForm(true); }}><Edit className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={async () => {
                if (!window.confirm("Automation löschen?")) return;
                await axios.delete(`${API}/api/automations/${a.id}`, { headers: headers() });
                toast.success("Gelöscht"); fetchAutomations();
              }}><Trash2 className="w-3 h-3 text-red-500" /></Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Protokoll ---
  const ProtokollView = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Ausführungsprotokoll</h2>
      <div className="space-y-2">
        {protokoll.length === 0 ? <p className="text-gray-400 text-sm">Noch keine Ausführungen</p> : protokoll.map((p, i) => (
          <Card key={i} className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {p.status === "erfolgreich" ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
              <div>
                <div className="text-sm font-medium">{p.automation_name}</div>
                <div className="text-xs text-gray-500">Trigger: {p.trigger} | Aktion: {p.aktion}</div>
              </div>
            </div>
            <div className="text-xs text-gray-400">{p.timestamp?.slice(0, 16).replace("T", " ")}</div>
          </Card>
        ))}
      </div>
    </div>
  );

  // --- Wartende Bestätigungen ---
  const PendingView = () => (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Wartende Bestätigungen</h2>
      <div className="space-y-2">
        {pending.length === 0 ? <p className="text-gray-400 text-sm">Keine wartenden Aktionen</p> : pending.map((p) => (
          <Card key={p.id} className="p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{p.automation_name}</div>
                <div className="text-xs text-gray-500">Trigger: {p.trigger} | {p.created_at?.slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={async () => {
                  await axios.post(`${API}/api/automations/pending/${p.id}/approve`, {}, { headers: headers() });
                  toast.success("Genehmigt"); fetchPending();
                }} className="bg-green-600 hover:bg-green-700">Genehmigen</Button>
                <Button size="sm" variant="outline" onClick={async () => {
                  await axios.post(`${API}/api/automations/pending/${p.id}/reject`, {}, { headers: headers() });
                  toast.success("Abgelehnt"); fetchPending();
                }}>Ablehnen</Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  const subTabs = [
    { id: "liste", label: "Automationen", icon: Zap },
    { id: "protokoll", label: "Protokoll", icon: List },
    { id: "wartend", label: `Wartend${pending.length > 0 ? ` (${pending.length})` : ""}`, icon: Clock },
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-2 overflow-x-auto pb-2 border-b">
        {subTabs.map((t) => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors ${subTab === t.id ? "bg-[var(--brand-primary)] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
            <t.icon className="w-3.5 h-3.5" />{t.label}
          </button>
        ))}
      </div>
      {subTab === "liste" && <ListeView />}
      {subTab === "protokoll" && <ProtokollView />}
      {subTab === "wartend" && <PendingView />}
    </div>
  );
}
